import { styled, Tab } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import QuestionIcon16pxSvg from "../../../assets/images/icons/QuestionIcon16pxSvg";
import DefaultTooltip from "../../../components/DefaultTooltip";
import {
  ControlpanelSecondaryButtonV2,
  ControlpanelSuccess3thButton,
} from "../../../components/StyledButton";

import { format } from "date-fns";
import { toast } from "react-toastify";
import CustomSelect from "../../../components/CustomSelect";
import { CustomTabsMini, NormalSwitch } from "../../../components/StyledComponents";
import CustomInput from "./../../../components/CustomInput";
import {
  getListEntitieById,
  resetSetting,
  updateSetting,
} from "../../../axios/Setting/SettingUrlApi";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import ConfirmResetAllDialog from "./ConfirmResetAllDialog";
import RefreshCircleIcon20pxSvgV2 from "../../../assets/images/icons/RefreshCircleIcon20pxSvgV2";
import CustomTimePickerHaveSecondV2 from "../../../components/CustomTimePickerHaveSecondV2";
import TimezoneData from "../../../assets/data/TimezoneData";
import { PropTypes } from 'prop-types';
import SystemConfigurationTab from "./SystemConfigurationTab";
import { AttendanceLeaveTab } from "./AttendanceLeaveTab";
import LeaveApi from "../../../axios/LeaveAndHoliday/LeaveApi";
import OrganizationTab from "./OrganizationTab";
import CustomSpinner from "../../../components/CustomSpinner";

const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div className='mt-3'>
          {children}
        </div>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const a11yProps = (index) => {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const CustomSecondaryButton = styled(ControlpanelSecondaryButtonV2)(() => ({
  minWidth: "40px",
  ".MuiSvgIcon-root": {
    fontSize: "20px",
  },
}));

const GeneralTab = ({ departmentTabId, tabId, tabNotiId, tabWebId, tabDepartmentId, handleRefresh, refreshData }) => {
  const [settingsData, setSettingsData] = useState([]);
  const { t, i18n } = useTranslation();
  const { refreshProfileSetting } = useAuth();
  const [confirmResetAllOpen, setConfirmResetAllOpen] = useState(false);
  const timezoneData = TimezoneData();
  const [tabValue, setTabValue] = useState(0)
  const [initialized, setInitialized] = useState(false);
  const [leaveTypesData, setLeaveTypesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getLeaveTypeData } = LeaveApi()

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  // Tour: chuyển sub-tab System / Attendance-Leave / Organization
  useEffect(() => {
    window.__setSettingsGeneralTab = (index) => {
      flushSync(() => setTabValue(index));
    };
    return () => {
      delete window.__setSettingsGeneralTab;
    };
  }, []);

  useEffect(() => {
    const fetchAllSettings = async () => {
      const ids = [tabId, tabNotiId, tabWebId, tabDepartmentId].filter(Boolean);
      if (!ids.length) return;

      setLoading(true);
      try {
        const responses = await Promise.all(
          ids.map((id) =>
            getListEntitieById({
              entityId: id,
              langId: localStorage.getItem("i18nextLngId"),
            })
          )
        );

        // Gộp tất cả settings lại + gắn entityId nguồn để update đúng entity
        const allSettings = responses.flatMap((res, index) => {
          const sourceEntityId = ids[index];
          return (res.data.data || []).map((item) => ({
            ...item,
            entityId: item.entityId ?? sourceEntityId,
          }));
        });

        setSettingsData(allSettings);

        // Map giống như cũ — key giữ nguyên tên BE
        const mapSettings = Object.fromEntries(
          allSettings.map((item) => [item.settingEntityTypeNameEN, item.value])
        );

        // Tra cứu value không phân biệt hoa/thường (tránh lệch casing từ BE)
        const getMapValue = (typeName, fallback = "") => {
          if (mapSettings[typeName] !== undefined && mapSettings[typeName] !== null) {
            return mapSettings[typeName];
          }
          const normalized = String(typeName).trim().toLowerCase();
          const matchedKey = Object.keys(mapSettings).find(
            (key) => String(key).trim().toLowerCase() === normalized
          );
          return matchedKey !== undefined ? mapSettings[matchedKey] : fallback;
        };

        const rawValue = getMapValue("Dashboard refresh interval", "0");
        const match = String(rawValue).match(/\d+/);
        const intervalNumber = match ? Number(match[0]) : 0;

        setFormData((prev) => ({
          ...prev,
          dateFormat: getMapValue("Date format", ""),
          timeFormat: getMapValue("Time format", ""),
          dayStartTime: getMapValue("Day start time", "01:00:00"),
          weekStartDate: getMapValue("Week start date", "Monday"),
          monthStartDay: Number(getMapValue("Month start date", "1")),
          yearStartMonth: getMapValue("Year start month", "January"),
          defaultShiftSelectionBehaviour:
            getMapValue("Default shift selection behaviour ", ""),
          autoCheckoutAtTheEndOfTheShift:
            getMapValue("Auto checkout at the end of the shift.", "0") !== "0",
          notifyCheckinAttemptsOnRestrictedLeave:
            getMapValue("Notify check-in attempts on restricted leave", "0") !== "0",
          unidentifiedEventlogCountToKeep: Number(
            getMapValue("Unidentified eventlog count to keep.", -1)
          ),
          urlInviteToJoinCompany:
            getMapValue("Url invite to join company", ""),
          timekeepingRequestExpirationTime: Number(
            getMapValue("Timekeeping request expiration time", 0)
          ),
          inviteEmailValidTime: Number(
            getMapValue("Invite email valid time", 0)
          ),
          dataReportPushTime: getMapValue("Data Report Push Time", ""),
          allowAutomatiApproval:
            getMapValue("Allow automatic approval of leave requests", "0") !== "0",
          timeZone: getMapValue("Timezone", ""),
          dashboardRefreshInterval: intervalNumber,
          smsNotification: (() => {
            try {
              const val = getMapValue("SMS Notifications");
              if (!val) return false;
              const parsed = JSON.parse(val);
              return parsed.enabled ?? true;
            } catch {
              return false;
            }
          })(),
          sendmailnotification:
            getMapValue("Send mail nofication") === "1",

          sendmailtocompanyadmin:
            getMapValue("Send mail to company admin") === "1",

          sendmailtogroupadmin:
            getMapValue("Send mail to group admin") === "1",

          sendmailtoanotheremailaddress:
            getMapValue("Send mail to another email address") === "1",
          leaveDeduction:
            getMapValue("Deduct leave for unauthorized absence", "0") !== "0",
          leaveType:
            getMapValue("The leave type to be deducted", ""),
          hoursPerLeaveDay:
            getMapValue("Time equivalent to one day of unauthorized absence", "00:00:00"),
          resetlateandearlytime: getMapValue("Reset unauthorized absence time", "0") !== "0",
          resetDate: getMapValue("Reset date", ""),
          mobileAttendanceWithinInternalIPNetwork:
            getMapValue("Mobile attendance within internal IP network", ""),
          leaveTypeWithoutPermission:
            getMapValue("Excluded Leave Types", ""),
          // BE: "Maximum Leave Duration" (viết hoa) — không dùng exact "Maximum leave duration"
          maximumLeaveDuration:
            getMapValue("Maximum Leave Duration", "00:00:00"),
          automaticallyConvertUnexcusedLeaveToExcusedLeave:
            getMapValue("Automatically Convert Unexcused Leave to Excused Leave", "0") !== "0",
        }));
      } catch (error) {
        toast.error("Fetch settings failed");
      } finally {
        setLoading(false);
      }
    };

    fetchAllSettings();
  }, [tabId, tabNotiId, tabWebId, tabDepartmentId, refreshData]);

  useEffect(() => {
    if (settingsData.length > 0) {
      const mapSettings = Object.fromEntries(
        settingsData.map((item) => [item.settingEntityTypeNameEN, item.value])
      );

      setFormData((prev) => ({
        ...prev,
        timekeepingRequestExpirationTime: Number(
          mapSettings["Timekeeping request expiration time"] || 0
        ),
      }));
    }
  }, [settingsData]);

  const getSettingItem = (typeName) => {
    if (!typeName) return null;
    const exact = settingsData.find(
      (item) => item.settingEntityTypeNameEN === typeName
    );
    if (exact) return exact;

    // Fallback: trim + ignore case (tránh lệch tên setting từ BE)
    const normalized = String(typeName).trim().toLowerCase();
    return (
      settingsData.find(
        (item) =>
          String(item.settingEntityTypeNameEN || "")
            .trim()
            .toLowerCase() === normalized
      ) || null
    );
  };

  const getAttrTypeId = (typeName) => {
    const item = getSettingItem(typeName);
    return item ? item.entityAttrTypeId : null;
  };

  // Lấy tiêu đề setting theo ngôn ngữ UI (EN/VN từ API), fallback i18n
  const getSettingTitle = useCallback((typeNameEN, fallbackKey) => {
    const item = getSettingItem(typeNameEN);
    const isVi = String(i18n.language || "").toLowerCase().startsWith("vi");
    if (item) {
      const label = isVi
        ? (item.settingEntityTypeNameVN || item.settingEntityTypeNameEN)
        : item.settingEntityTypeNameEN;
      if (label) return label;
    }
    return fallbackKey ? t(fallbackKey) : (typeNameEN || "");
  }, [settingsData, i18n.language, t]);

  // Tiêu đề section từ settingEntityNameEN/VN của một setting đại diện
  const getSettingEntityTitle = useCallback((typeNameEN, fallbackKey) => {
    const item = getSettingItem(typeNameEN);
    const isVi = String(i18n.language || "").toLowerCase().startsWith("vi");
    if (item) {
      const label = isVi
        ? (item.settingEntityNameVN || item.settingEntityNameEN)
        : item.settingEntityNameEN;
      if (label) return label;
    }
    return fallbackKey ? t(fallbackKey) : "";
  }, [settingsData, i18n.language, t]);

  const [formData, setFormData] = useState({
    dateFormat: "",
    timeFormat: "",
    dayStartTime: "00:00",
    weekStartDate: "Monday",
    monthStartDay: 1,
    yearStartMonth: "January",
    defaultShiftSelectionBehaviour: "",
    autoCheckoutAtTheEndOfTheShift: false,
    notifyCheckinAttemptsOnRestrictedLeave: false,
    unidentifiedEventlogCountToKeep: -1,
    allowAutomatiApproval: false,
    dashboardRefreshInterval: 0,
    smsNotification: false,
    sendmailnotification: false,
    sendmailtocompanyadmin: false,
    sendmailtogroupadmin: false,
    sendmailtoanotheremailaddress: false,
    leaveDeduction: false,
    leaveType: "",
    hoursPerLeaveDay: "00:00:00",
    resetlateandearlytime: false,
    resetDate: "",
    mobileAttendanceWithinInternalIPNetwork: "",
    leaveTypeWithoutPermission: "",
    maximumLeaveDuration: "00:00:00",
  });

  const settingKeyMap = {
    dateFormat: "Date format",
    timeFormat: "Time format",
    dayStartTime: "Day start time",
    weekStartDate: "Week start date",
    monthStartDay: "Month start date",
    yearStartMonth: "Year start month",
    defaultShiftSelectionBehaviour: "Default shift selection behaviour ",
    autoCheckoutAtTheEndOfTheShift: "Auto checkout at the end of the shift.",
    notifyCheckinAttemptsOnRestrictedLeave:
      "Notify check-in attempts on restricted leave",
    unidentifiedEventlogCountToKeep: "Unidentified eventlog count to keep.",
    urlInviteToJoinCompany: "Url invite to join company",
    timekeepingRequestExpirationTime: "Timekeeping request expiration time",
    inviteEmailValidTime: "Invite email valid time",
    dataReportPushTime: "Data Report Push Time",
    allowAutomatiApproval: "Allow automatic approval of leave requests",
    timeZone: "Timezone",
    dashboardRefreshInterval: "Dashboard refresh interval",
    smsNotification: "SMS Notifications",
    sendmailnotification: "Send mail nofication",
    sendmailtocompanyadmin: "Send mail to company admin",
    sendmailtogroupadmin: "Send mail to group admin",
    sendmailtoanotheremailaddress: "Send mail to another email address",
    leaveDeduction: "Deduct leave for unauthorized absence",
    leaveType: "The leave type to be deducted",
    hoursPerLeaveDay: "Time equivalent to one day of unauthorized absence",
    resetlateandearlytime: "Reset unauthorized absence time",
    resetDate: "Reset date",
    mobileAttendanceWithinInternalIPNetwork: "Mobile attendance within internal IP network",
    leaveTypeWithoutPermission: "Excluded Leave Types",
    maximumLeaveDuration: "Maximum Leave Duration",
    automaticallyConvertUnexcusedLeaveToExcusedLeave: "Automatically Convert Unexcused Leave to Excused Leave",
  };

  const handleAutoSave = async (settingKey, value) => {
    const settingItem = getSettingItem(settingKey);
    const entityAttrTypeId = settingItem?.entityAttrTypeId;
    if (!entityAttrTypeId) {
      console.warn(`No entityAttrTypeId found for ${settingKey}`);
      return;
    }

    let contentUpdate = value;
    const keyName = Object.keys(settingKeyMap).find(
      (k) => settingKeyMap[k] === settingKey
    );

    // Switch/boolean: true -> "1", false -> "0"
    if (
      typeof value === "boolean" ||
      keyName === "autoCheckoutAtTheEndOfTheShift" ||
      keyName === "notifyCheckinAttemptsOnRestrictedLeave" ||
      keyName === "allowAutomatiApproval" ||
      keyName === "leaveDeduction" ||
      keyName === "resetlateandearlytime" ||
      keyName === "automaticallyConvertUnexcusedLeaveToExcusedLeave"
    ) {
      contentUpdate = value ? "1" : "0";
    }
    if (typeof contentUpdate === "number") {
      contentUpdate = String(contentUpdate);
    }

    const payload = {
      // Dùng entityId của đúng setting (không luôn ép tabId)
      entityId: settingItem.entityId ?? tabId,
      entityAttributeTypeId: entityAttrTypeId,
      value: contentUpdate,
      langId: localStorage.getItem("i18nextLngId"),
    };
    try {
      const response = await updateSetting(payload);
      if (response?.data?.responseCode === 0) {
        toast.success(t("common.update-setting-successfully"));
        handleRefresh();
        refreshProfileSetting();
      } else {
        toast.error(response?.data?.message);
        // Optional: Revert state on error (requires storing old value)
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
      // Optional: Revert
    }
  };

  const handleAutoSaveWeb = async (settingKey, value) => {
    const entityAttrTypeId = getAttrTypeId(settingKey);
    if (!entityAttrTypeId) {
      console.warn(`No entityAttrTypeId found for ${settingKey}`);
      return;
    }

    let contentUpdate = value;
    const keyName = Object.keys(settingKeyMap).find(
      (k) => settingKeyMap[k] === settingKey
    );

    const payload = {
      entityId: tabWebId,
      entityAttributeTypeId: entityAttrTypeId,
      value: contentUpdate,
      langId: localStorage.getItem("i18nextLngId"),
    };
    try {
      const response = await updateSetting(payload);
      if (response?.data?.responseCode === 0) {
        toast.success(t("common.update-setting-successfully"));
        handleRefresh();
        refreshProfileSetting();
      } else {
        toast.error(response?.data?.message);
        // Optional: Revert state on error (requires storing old value)
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
      // Optional: Revert
    }
  };

  const handleAutoSaveNoti = async (settingKey, value) => {
    const entityAttrTypeId = getAttrTypeId(settingKey);
    if (!entityAttrTypeId) return;

    let contentUpdate = value;

    const keyName = Object.keys(settingKeyMap).find(
      (k) => settingKeyMap[k] === settingKey
    );

    // ✅ SMS (JSON)
    if (keyName === "smsNotification") {
      const currentSetting = settingsData.find(
        (item) => item.settingEntityTypeNameEN === "SMS Notifications"
      );

      try {
        const parsed = JSON.parse(currentSetting.value);
        parsed.enabled = value;
        contentUpdate = JSON.stringify(parsed);
      } catch (e) {
        console.error("Parse SMS JSON failed", e);
        return;
      }
    }

    // ✅ FIX CHO EMAIL (QUAN TRỌNG)
    if (
      keyName === "sendmailnotification" ||
      keyName === "sendmailtocompanyadmin" ||
      keyName === "sendmailtogroupadmin" ||
      keyName === "sendmailtoanotheremailaddress"
    ) {
      contentUpdate = value ? "1" : "0";
    }

    const payload = {
      entityId: tabNotiId,
      entityAttributeTypeId: entityAttrTypeId,
      value: contentUpdate,
      langId: localStorage.getItem("i18nextLngId"),
    };

    try {
      const response = await updateSetting(payload);
      if (response?.data?.responseCode === 0) {
        toast.success(t("common.update-setting-successfully"));
        handleRefresh();
        refreshProfileSetting();
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      toast.error("Update failed");
    }
  };

  const handleInputChange = (e, type = "default") => {
    const { name, checked, type: inputType } = e.target;
    const value = inputType === "checkbox" ? checked : e.target.value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    const settingKey = settingKeyMap[name];
    if (settingKey) {
      debouncedAutoSave(settingKey, value, type);
    }
  };

  const debounceRef = useRef(null);

  const debouncedAutoSave = (settingKey, value, type = "default") => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (type === "web") {
        handleAutoSaveWeb(settingKey, value);
      } else if (type === "noti") {
        handleAutoSaveNoti(settingKey, value);
      } else {
        handleAutoSave(settingKey, value);
      }
    }, 700);
  };


  const handleDataChange = (name, newData) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newData,
    }));

    const settingKey = settingKeyMap[name];
    if (settingKey) {
      debouncedAutoSave(settingKey, newData);
    }
  };


  const dateFormatData = [
    { value: "dd/MM/yyyy", label: "dd/MM/yyyy" },
    { value: "dd-MM-yyyy", label: "dd-MM-yyyy" },
    { value: "yyyy-MM-dd", label: "yyyy-MM-dd" },
    { value: "dd.MM.yyyy", label: "dd.MM.yyyy" },
    { value: "MM-dd-yyyy", label: "MM-dd-yyyy" },
  ];

  const timeFormatData = [
    { value: "HH:mm:ss", label: "HH:mm:ss" },
    { value: "H:mm a", label: "H:mm a" },
    { value: "HH:mm", label: "HH:mm" },
    { value: "h:mm:ss a", label: "h:mm:ss a" },
    { value: "H:mm:ss", label: "H:mm:ss" },
    { value: "H:mm", label: "H:mm" },
  ];

  const weekStartDateData = [
    { value: "Monday", label: t("common.monday") },
    { value: "Tuesday", label: t("common.tuesday") },
    { value: "Wednesday", label: t("common.wednesday") },
    { value: "Thursday", label: t("common.thursday") },
    { value: "Friday", label: t("common.friday") },
    { value: "Saturday", label: t("common.saturday") },
    { value: "Sunday", label: t("common.sunday") },
  ];

  const monthStartDateData = Array.from({ length: 28 }, (v, k) => ({
    value: k + 1,
    label: (k + 1).toString(),
  }));

  const yearStartMonthData = [
    { value: "January", label: t("common.january") },
    { value: "February", label: t("common.february") },
    { value: "March", label: t("common.march") },
    { value: "April", label: t("common.april") },
    { value: "May", label: t("common.may") },
    { value: "June", label: t("common.june") },
    { value: "July", label: t("common.july") },
    { value: "August", label: t("common.august") },
    { value: "September", label: t("common.september") },
    { value: "October", label: t("common.october") },
    { value: "November", label: t("common.november") },
    { value: "December", label: t("common.december") },
  ];

  const defaultShiftSelectionBehaviourData = [
    { value: "Don't allow", label: t("common.dontAllow") },
    {
      value: "Allow when no shifts available",
      label: t("common.allowWhenNoShiftsAvailable"),
    },
    {
      value: "Allow when other shifts available",
      label: t("common.allowWhenOtherShiftsAvailable"),
    },
  ];

  const handleResetSetting = async (entityAttrTypeId, keyName) => {
    const payload = {
      entityId: tabId,
      entityAttributeTypeId: entityAttrTypeId,
      langId: localStorage.getItem("i18nextLngId"),
    };
    setLoading(true);
    try {
      const response = await resetSetting(payload);
      if (response?.data?.responseCode === 0) {
        toast.success(t("common.reset-setting-successfully"));
        handleRefresh();
        refreshProfileSetting();
        // window.location.reload()
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetWebSetting = async (entityAttrTypeId, keyName) => {
    const payload = {
      entityId: tabWebId,
      entityAttributeTypeId: entityAttrTypeId,
      langId: localStorage.getItem("i18nextLngId"),
    };
    setLoading(true);
    try {
      const response = await resetSetting(payload);
      if (response?.data?.responseCode === 0) {
        toast.success(t("common.reset-setting-successfully"));
        handleRefresh();
        refreshProfileSetting();
        // window.location.reload()
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response.data));
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetNoti = async (entityAttrTypeId) => {
    const payload = {
      entityId: tabNotiId,
      entityAttributeTypeId: entityAttrTypeId,
      langId: localStorage.getItem("i18nextLngId"),
    };

    setLoading(true);
    try {
      const response = await resetSetting(payload);
      if (response?.data?.responseCode === 0) {
        toast.success(t("common.reset-setting-successfully"));
        handleRefresh();
        refreshProfileSetting();
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      toast.error("Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResetAll = async () => {
    setLoading(true);
    const promises = settingsData.map(async (item) => {
      const payload = {
        entityId: tabId,
        entityAttributeTypeId: item.entityAttrTypeId,
        langId: localStorage.getItem("i18nextLngId"),
      };
      return resetSetting(payload);
    });

    try {
      const responses = await Promise.all(promises);
      const allSuccessful = responses.every((r) => r?.data?.responseCode === 0);
      if (allSuccessful) {
        toast.success(t("common.reset-setting-successfully"));
        handleRefresh();
        refreshProfileSetting();
      } else {
        toast.error("Some settings failed to reset");
      }
    } catch (error) {
      toast.error("Failed to reset all settings");
    } finally {
      setLoading(false);
      setConfirmResetAllOpen(false);
    }
  };

  const handleCloseConfirmResetAll = () => {
    setConfirmResetAllOpen(false);
  };

  const handleOpenConfirmResetAll = () => {
    setConfirmResetAllOpen(true);
  };

  const fetchLeaveTypeData = async (isDeleted = true) => {
    const payload = {
      langId: localStorage.getItem("i18nextLngId"),
      isDeleted: true
    };
    try {
      const response = await getLeaveTypeData(payload);
      if (
        response?.data?.message === "Successful" ||
        response?.data?.message === "Thành công"
      ) {
        setLeaveTypesData(
          (response.data.data ?? []).filter(item => item.leaveTypeId !== 1)
        );
      }
    } catch (error) {
      if (error?.response) {
        if (error.response.status !== 401) {
          toast.error(error?.response?.data?.message || "Error");
        }
      } else {
        toast.error(t('common.network-timeout'));
      }
      console.error("Error fetching leave types:", error);
    }
  };

  useEffect(() => {
    fetchLeaveTypeData();
  }, [t]);

  return (
    <>
      <div className="general-tab relative flex flex-col min-h-[70.5vh]">
        <CustomSpinner loading={loading} />
        <div className="flex md:flex-row flex-col gap-2 justify-between pt-3 pointer-events-auto mb-3">
          <div>
            <CustomTabsMini
              variant="scrollable"
              indicatorColor='none'
              value={tabValue}
              onChange={handleChangeTab}>
              <Tab
                label={t('common.systemConfiguration')}
                {...a11yProps(0)}
                disableRipple
                data-tour="setting-system-configuration"
              />
              <Tab
                label={t('common.attendanceLeave')}
                {...a11yProps(1)}
                disableRipple
                data-tour="setting-attendance-leave"
              />
              <Tab
                label={t('common.organizationManagement')}
                {...a11yProps(2)}
                disableRipple
                data-tour="setting-organization"
              />
            </CustomTabsMini>
          </div>
          <div>
            <ControlpanelSuccess3thButton onClick={handleOpenConfirmResetAll} sx={{
              pointerEvents: "auto",
              opacity: 0.5,
              pointerEvents: "none",
            }}>
              <div className="flex gap-2"><RefreshCircleIcon20pxSvgV2 className="text-[#0057FF]"></RefreshCircleIcon20pxSvgV2>
                <p>{t("button.reset-all-to-default")}</p></div>
            </ControlpanelSuccess3thButton>
          </div>
        </div>
        <CustomTabPanel value={tabValue} index={0}>
          <SystemConfigurationTab handleResetAll={handleResetAll} refreshData={refreshData} handleResetNoti={handleResetNoti} timeFormatData={timeFormatData} dateFormatData={dateFormatData} yearStartMonthData={yearStartMonthData} monthStartDateData={monthStartDateData} weekStartDateData={weekStartDateData} settingKeyMap={settingKeyMap} handleDataChange={handleDataChange} debouncedAutoSave={debouncedAutoSave}
            formData={formData} handleInputChange={handleInputChange} handleResetSetting={handleResetSetting}
            handleResetWebSetting={handleResetWebSetting} getAttrTypeId={getAttrTypeId}
            getSettingTitle={getSettingTitle} getSettingEntityTitle={getSettingEntityTitle} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={1}>
          <AttendanceLeaveTab handleResetWebSetting={handleResetWebSetting} settingKeyMap={settingKeyMap} debouncedAutoSave={debouncedAutoSave} handleDataChange={handleDataChange} refreshData={refreshData} handleRefresh={handleRefresh} leaveTypesData={leaveTypesData} defaultShiftSelectionBehaviourData={defaultShiftSelectionBehaviourData} handleInputChange={handleInputChange} formData={formData} getAttrTypeId={getAttrTypeId} handleResetSetting={handleResetSetting}
            getSettingTitle={getSettingTitle} getSettingEntityTitle={getSettingEntityTitle} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={2}>
          <OrganizationTab departmentTabId={departmentTabId} tabId={tabId} refreshData={refreshData} handleRefresh={handleRefresh} />
        </CustomTabPanel>

      </div>
      {confirmResetAllOpen && (
        <ConfirmResetAllDialog
          onClose={handleCloseConfirmResetAll}
          onConfirm={handleResetAll}
          loading={loading}
        />
      )}
    </>
  );
};

export default GeneralTab;
